import {
  CalculationStipulantsListSection,
  CalculationSummaryCardSection,
} from '@/features/quotes/components/capital-global/steps/sections/step-calculation.sections';
import { CloneVersionsSection } from '@/features/quotes/components/quote-steps/group-life/clone-versions-section.component';
import type { PmeProposalDetails } from '@/features/proposals/hooks/pme/use-pme-proposal-details-view-model.hook';
import type { GroupLifeQuoteDetails } from '@/services/group-life';
import { PremiumCard } from '../../../proposal-steps/premium-card.component';
import { PmeLivesByStipulantSection } from './lives-by-stipulant-section.component';
import {
  PmeCommercializationSection,
  PmeDemonstrativeSection,
} from './resume-tables.component';

type Props = {
  /** Derivações prontas — ver `usePmeProposalDetailsViewModel`. */
  sections: PmeProposalDetails;
  policyId: string;
  details?: GroupLifeQuoteDetails;
  proposalNumber?: string;
  isUnderAnalysis?: boolean;
  isRecalculating?: boolean;
  /** Mostra as colunas de subscrição na comercialização. */
  showUnderwritingFields?: boolean;
  /** Libera incluir vida e importar planilha. Falso deixa a tabela só leitura. */
  canManageLives?: boolean;
  onLivesChange?: (outcome: { requiresRecalculation: boolean }) => void;
  onImportCompleted?: (outcome: { requiresRecalculation: boolean }) => void;
};

/**
 * Detalhamento da proposta PME — componente puramente apresentacional.
 *
 * É o corpo compartilhado entre o Resumo e a Assinatura: as duas telas mostram
 * exatamente o mesmo detalhamento, e o que muda fica fora daqui (o rodapé de
 * cada uma e, na Assinatura, os documentos em coleta). Não busca dado nem
 * decide regra: recebe tudo pronto.
 */
export function PmeProposalDetailsSections({
  sections,
  policyId,
  details,
  proposalNumber,
  isUnderAnalysis,
  isRecalculating,
  showUnderwritingFields = false,
  canManageLives = false,
  onLivesChange,
  onImportCompleted,
}: Props) {
  const {
    premium,
    insuranceCard,
    stipulantCard,
    accordions,
    demonstrativeRows,
    commercializationRows,
    lives,
  } = sections;

  return (
    <>
      <PremiumCard
        totalValue={premium.totalValue}
        totalLabel='Prêmio Mensal'
        liquidValue={premium.liquidValue}
        interestPercentage='—'
        interestValue='—'
        iofValue={premium.iofValue}
        product='PME'
        proposalNumber={proposalNumber ?? '-'}
        proposalLabel='Número da Cotação'
        status={premium.status}
        defaultExpanded
        showPisCofins={false}
        isUnderAnalysis={isUnderAnalysis}
        isRecalculating={isRecalculating}
      />

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <CalculationSummaryCardSection data={insuranceCard} />
        <CalculationSummaryCardSection data={stipulantCard} />
      </div>

      {/* Versões — cadeia de clonagem (apenas clones) */}
      {details?.clonedFrom?.length ? (
        <CloneVersionsSection
          clonedFrom={details.clonedFrom}
          current={{
            policyId,
            kind: 'PROPOSAL',
            proposalNumber: proposalNumber || details.proposalNumber,
            statusPortal: details.statusPortal,
          }}
        />
      ) : null}

      <CalculationStipulantsListSection entries={accordions} />

      <PmeLivesByStipulantSection
        details={details}
        lives={lives}
        policyId={details?.policyId || policyId}
        plan={details?.plan}
        onLivesChange={onLivesChange}
        onImportCompleted={onImportCompleted}
        showAddMember={canManageLives}
        showImport={canManageLives}
        pageSize={10}
        // Proposta: nome é obrigatório em toda vida — manual e por planilha.
        requireName
      />

      <PmeDemonstrativeSection
        rows={demonstrativeRows}
        isRecalculating={isRecalculating}
      />

      {/* Agravo/desconto gravados na apólice (`PolicyDiscountLoadingList`).
          Sem eles a seção caía no "0,00 %" fixo, mesmo com o motor tendo
          aplicado agravo. */}
      <PmeCommercializationSection
        rows={commercializationRows}
        showUnderwritingFields={showUnderwritingFields}
        surcharge={details?.commercialization?.surchargePercent}
        actuarialDiscount={details?.commercialization?.actuarialDiscountPercent}
      />
    </>
  );
}
